import { useCallback } from 'react'
import axios from 'axios'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { extractErrorMessage } from '../utils/apitools'
import useAccount from './useAccount'
import useSnackbar from './useSnackbar'
import { API_BASE_URL } from '../constants/system'
import { ChartTemplate } from '../types/gotypes'
import { ChartTemplateCapture } from '../utils/chartTemplate'

interface ListChartTemplatesResponse {
  customer: string
  chart_templates: ChartTemplate[]
  count: number
}

/**
 * Personal + company-level chart templates (#1050) — `GET/POST
 * /dashboards/:customer/chart-templates` plus promote/demote/delete.
 * Deliberately its own hook rather than reusing useDashboards: chart
 * templates are a different resource entirely (design-only, no
 * DashboardConfig), not a row in the dashboards table — see
 * types.ChartTemplate / goapi/pkg/server/chart_templates.go.
 */
export const useChartTemplates = ({ customer }: { customer: string }) => {
  const account = useAccount()
  const snackbar = useSnackbar()
  const queryClient = useQueryClient()

  const listQueryKey = ['chart-templates', { customer }]

  const listQuery = useQuery({
    queryKey: listQueryKey,
    enabled: Boolean(customer && account.user),
    queryFn: async () => {
      const res = await axios.get<ListChartTemplatesResponse>(
        `${API_BASE_URL}/dashboards/${customer}/chart-templates`
      )
      return res.data.chart_templates
    },
  })

  const createMutation = useMutation({
    mutationFn: async (capture: ChartTemplateCapture) => {
      const res = await axios.post<ChartTemplate>(`${API_BASE_URL}/dashboards/${customer}/chart-templates`, capture)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listQueryKey })
    },
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
    },
  })

  const promoteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await axios.post<ChartTemplate>(`${API_BASE_URL}/dashboards/${customer}/chart-templates/${id}/promote`)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listQueryKey })
    },
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
    },
  })

  const demoteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await axios.delete<ChartTemplate>(`${API_BASE_URL}/dashboards/${customer}/chart-templates/${id}/promote`)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listQueryKey })
    },
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(`${API_BASE_URL}/dashboards/${customer}/chart-templates/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listQueryKey })
    },
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
    },
  })

  const createChartTemplate = useCallback(
    async (capture: ChartTemplateCapture) => await createMutation.mutateAsync(capture),
    [createMutation]
  )
  const promoteChartTemplate = useCallback(async (id: string) => await promoteMutation.mutateAsync(id), [promoteMutation])
  const demoteChartTemplate = useCallback(async (id: string) => await demoteMutation.mutateAsync(id), [demoteMutation])
  const deleteChartTemplate = useCallback(async (id: string) => await deleteMutation.mutateAsync(id), [deleteMutation])

  return {
    chartTemplates: listQuery.data || [],
    chartTemplatesLoading: listQuery.isLoading,
    chartTemplatesIsError: listQuery.isError,

    createChartTemplate,
    createChartTemplateIsLoading: createMutation.isPending,

    promoteChartTemplate,
    demoteChartTemplate,
    deleteChartTemplate,
  }
}

export default useChartTemplates
